import { useEffect, useState } from 'react';
import 'react-popper-tooltip/dist/styles.css';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Button from '../../../../components/buttons/Button';
import { ErrorMessage } from '../../../../components/ErrorMessage/ErrorMessage';
import { UiRedesignPopperTooltip } from '../../../../components/Tooltip/UiRedesignPopperTooltip';
import { analytics } from '../../../../services/analytics';
import { useStores } from '../../../../stores/useStores';
import { checkIfAppleDevice } from '../../../../utils/helpers';
import { isInAppBrowser } from '../../../../utils/isInAppBrowser';
import { useHandleAuth } from '../../useHandleAuth';
import { useHandleSignUpWizardRedesign } from '../../wizards/useHandleSignUpWizardRedesign';

import appleIcon from './appleIcon.svg';
import facebookIcon from './facebookIcon.svg';
import googleIcon from './googleIcon.svg';
import { InAppBlockedTooltipContent } from './InAppBlockedTooltip/InAppBlockedTooltip';
import styles from './ThirdPartyAuthRedesign.module.scss';

type ThirdPartyAuthProvider = 'apple' | 'google' | 'facebook';

export const ThirdPartyAuthRedesign = observer(function ThirdPartyAuthRedesign({
  action,
  excludedProviders = [],
  onLoginAttempt,
  className,
}: {
  action: 'Sign Up' | 'Log in';
  excludedProviders?: ThirdPartyAuthProvider[];
  onLoginAttempt?: () => boolean;
  className?: string;
}) {
  const { authStore } = useStores();
  const { t } = useTranslation();

  const { isSigningUp } = authStore;

  const [error, setError] = useState<string>();

  const { handleAuth } = useHandleAuth(setError);
  const { handleSignUpWizard } = useHandleSignUpWizardRedesign();

  const isGoogleSignInBlocked = isInAppBrowser;

  const handleSocialSignIn = (provider: 'apple' | 'google' | 'facebook') => () => {
    if (isGoogleSignInBlocked && provider === 'google') {
      return;
    }

    const actionFormatted = action === 'Sign Up' ? 'SIGN_UP' : 'LOG_IN';

    if (actionFormatted === 'SIGN_UP' && typeof onLoginAttempt === 'function') {
      const isValid = onLoginAttempt();

      if (!isValid) {
        return;
      }
    }

    if (actionFormatted === 'SIGN_UP') {
      handleSignUpWizard({ provider, action: 'sign_up' });
    } else {
      void analytics.sendEvent('login_account', {
        event_action: 'click',
        event_category: 'sign_up',
        event_type: 'login_platform',
        event_value: provider,
      });
      void handleAuth({ provider, action: 'log_in' });
    }
  };

  useEffect(() => {
    authStore.setPending('thirdPartyAuth', false);
  }, [authStore]);

  return (
    <div className={classNames(className)}>
      <div className={styles.thirdPartyAuthButtonsWrapper}>
        {checkIfAppleDevice() && !excludedProviders.includes('apple') && (
          <Button
            btnType="light"
            className={styles.thirdPartyAuthButton}
            classNameLeftIcon={classNames(styles.iconContainer)}
            data-testid={action === 'Sign Up' ? 'signup-apple-btn' : 'login-apple-btn'}
            disabled={isSigningUp}
            iconLeft={
              <img alt="apple-icon" className={classNames(styles.iconImg, styles.iconImgApple)} src={appleIcon} />
            }
            onClick={handleSocialSignIn('apple')}
          >
            {t('account.authApple')}
          </Button>
        )}

        {!excludedProviders.includes('google') && (
          <>
            <UiRedesignPopperTooltip
              isBig
              childrenClassName={styles.thirdPartyAuthGoogleButtonContainer}
              content={<InAppBlockedTooltipContent />}
              disabled={!isGoogleSignInBlocked}
              wrapperClassName={styles.thirdPartyAuthGoogleButtonWrapper}
            >
              <Button
                btnType="light"
                className={styles.thirdPartyAuthButton}
                classNameLeftIcon={styles.iconContainer}
                data-testid={action === 'Sign Up' ? 'signup-google-btn' : 'login-google-btn'}
                disabled={isSigningUp}
                iconLeft={<img alt="google-icon" className={styles.iconImg} src={googleIcon} />}
                onClick={handleSocialSignIn('google')}
              >
                {t('account.authGoogle')}
              </Button>
            </UiRedesignPopperTooltip>
          </>
        )}

        {!excludedProviders.includes('facebook') && (
          <Button
            btnType="blue"
            className={classNames(styles.thirdPartyAuthButton, styles.facebookAuthButton)}
            classNameLeftIcon={classNames(styles.iconContainer, styles.iconFacebookContainer)}
            data-testid={action === 'Sign Up' ? 'signup-facebook-btn' : 'login-facebook-btn'}
            disabled={isSigningUp}
            iconLeft={
              <img
                alt="facebook-icon"
                className={classNames(styles.iconImg, styles.iconImgFacebook)}
                src={facebookIcon}
              />
            }
            onClick={handleSocialSignIn('facebook')}
          >
            {t('account.authFacebook')}
          </Button>
        )}
      </div>
      <ErrorMessage noSpaceWhenEmpty>{error}</ErrorMessage>
    </div>
  );
});
